/**
 * Multi-select preset folders + create
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useState } from 'react';

import { Button, Input } from '@/components/ui';
import { useCreatePresetFolder, usePresetFolders } from '@/hooks';

export interface PresetFoldersPickerProps {
  countryKey: string;
  value: string[];
  onChange: (folderIds: string[]) => void;
  disabled?: boolean;
}

export const PresetFoldersPicker: FC<PresetFoldersPickerProps> = ({
  countryKey,
  value,
  onChange,
  disabled = false,
}) => {
  const foldersQuery = usePresetFolders(countryKey);
  const createFolder = useCreatePresetFolder(countryKey);
  const [newLabel, setNewLabel] = useState('');
  const list = foldersQuery.data ?? [];

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  const handleCreate = async () => {
    const t = newLabel.trim();
    if (!t || createFolder.isPending) return;
    try {
      const row = await createFolder.mutateAsync(t);
      onChange([...value, row.id]);
      setNewLabel('');
    } catch {
      /* toast optional */
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-muted-foreground">Preset folders</span>
      <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
        {list.map((t) => (
          <button
            key={t.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(t.id)}
            className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${
              value.includes(t.id)
                ? 'border-primary bg-primary/15 text-primary'
                : 'border-border bg-background text-muted-foreground hover:bg-muted/50'
            }`}
          >
            {t.label}
          </button>
        ))}
        {list.length === 0 && !foldersQuery.isLoading && (
          <span className="text-xs text-muted-foreground">No folders yet — create one below.</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="New folder"
          maxLength={80}
          className="h-8 text-xs"
          disabled={disabled}
          onKeyDown={(e) => e.key === 'Enter' && void handleCreate()}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 text-xs"
          disabled={disabled || !newLabel.trim() || createFolder.isPending}
          onClick={() => void handleCreate()}
        >
          Add
        </Button>
      </div>
    </div>
  );
};
